import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import {
  Checkbox,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Separator,
} from '../'
import { Popover, PopoverContent, PopoverTrigger } from '../'

import Image from 'next/image'
import arrow from 'public/arrow=20.svg'
import { cn } from '../utils'

interface ArrowDownProps {
  isOpen?: boolean
}

export const ArrowDown = ({ isOpen }: ArrowDownProps) => {
  return (
    <Image
      src={arrow}
      alt="Chevron Down"
      // width={24}
      // height={24}
      className={cn(`cursor-pointer`, isOpen ? 'animate-rotate180' : 'animate-rotate0')}
      // className={`cursor-pointer ${isPopoverOpen ? 'animate-rotate180' : 'animate-rotate0'}`}
    />
  )
}

const multiSelectVariants = cva(
  'm-1 transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 duration-300',
  {
    variants: {
      variant: {
        default: 'border-[#BFD4D6] drop-shadow-md text-primary bg-card ',
        secondary: 'border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive: 'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        inverted: 'inverted',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

interface MultiSelectFormFieldProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectVariants> {
  asChild?: boolean
  options: string[]
  defaultValue?: string[]
  disabled?: boolean
  placeholder: string
  required?: boolean
  popoverContentClassName?: string
  commandClassName?: string
  className?: string
  animation?: number
  onValueChange: (value: string[]) => void
}

export const MultiSelectFormListStringFieldOneLineUiTest = React.forwardRef<
  HTMLButtonElement,
  MultiSelectFormFieldProps
>(
  (
    {
      className,
      variant,
      asChild = false,
      options,
      defaultValue,
      onValueChange,
      disabled,
      placeholder,
      required,
      animation = 0,
			popoverContentClassName,
			commandClassName,
      ...props
    },
    ref,
  ) => {
    const [selectedValues, setSelectedValues] = React.useState<string[]>(defaultValue || [])
    const selectedValuesSet = React.useRef(new Set(selectedValues))
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false)
    const [isAnimating, setIsAnimating] = React.useState(animation > 0)
    const [searchQuery, setSearchQuery] = React.useState('') // Добавлено состояние для поиска

    React.useEffect(() => {
      setSelectedValues(defaultValue || [])
      selectedValuesSet.current = new Set(defaultValue)
    }, [defaultValue])

    const handleInputKeyDown = (event: any) => {
      if (event.key === 'Enter') {
        setIsPopoverOpen(true)
        // } else if (event.key === 'Backspace' && !searchQuery) {
      } else if (event.key === 'Backspace' && !event.target.value) {
        selectedValues.pop()
        setSelectedValues([...selectedValues])
        selectedValuesSet.current.delete(selectedValues[selectedValues.length - 1])
        onValueChange([...selectedValues])
      }
    }

    const toggleOption = (value: string) => {
      if (selectedValuesSet.current.has(value)) {
        selectedValuesSet.current.delete(value)
        setSelectedValues(selectedValues.filter(v => v !== value))
      } else {
        selectedValuesSet.current.add(value)
        setSelectedValues([...selectedValues, value])
      }
      onValueChange(Array.from(selectedValuesSet.current))
    }

    // Фильтрация опций на основе searchQuery
    const filteredOptions = () => {
      if (!searchQuery) return options
      console.log('searchQuery', searchQuery)
      const filteredOptions = options.filter(option => option.toLowerCase().includes(searchQuery.toLowerCase())) ?? []
      console.log('filteredOptions', filteredOptions)
      return filteredOptions
    }
    // const filteredOptions = React.useMemo(() => {
    //   if (!searchQuery) return options
    //   // console.log('searchQuery', searchQuery)
    //   const filteredOptions = options.filter(option => option.toLowerCase().includes(searchQuery.toLowerCase())) ?? []
    //   // console.log('filteredOptions', filteredOptions)
    //   return filteredOptions
    // }, [options, searchQuery])
    return (
      <Popover
        open={isPopoverOpen}
        modal={true}
        onOpenChange={setIsPopoverOpen}
      >
        <PopoverTrigger
          className={cn(' min-w-[var(--radix-popper-anchor-width)]', className)}
          asChild
        >
          <button
            ref={ref}
            className="leading-input text-input flex h-auto w-full flex-1 items-center justify-between border-b border-[#BFD4D6] bg-inherit p-1 px-0 py-3 font-light focus-visible:ring-ring"
            // className="leading-input text-input flex h-auto w-full flex-1 items-center justify-between border-b border-primary bg-inherit p-1 px-0 py-3 font-light focus-visible:ring-ring"
            {...props}
            aria-required={required}
            onClick={() => setIsPopoverOpen(!isPopoverOpen)}
          >
            <div className="flex w-full items-center justify-between gap-1">
              {selectedValues.length > 0 ? (
                <div className="overflow-hidden text-ellipsis whitespace-nowrap text-primary">
                  {selectedValues.join(', ')}
                </div>
              ) : (
                <span className=" text-input text-[#80A8AE] text-muted-foreground">{placeholder}</span>
              )}
              <ArrowDown isOpen={isPopoverOpen} />
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className={cn("custom-scrollbar min-w-[var(--radix-popper-anchor-width)] p-0", popoverContentClassName)}
          align="start"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command
            shouldFilter={false}
            className={cn("w-full", commandClassName)}
          >
            {options.length > 10 && (
              <CommandInput
                placeholder="Search..."
                value={searchQuery}
                onValueChange={e => setSearchQuery(e)} // Обработчик изменения ввода
                onKeyDown={handleInputKeyDown}
              />
            )}
            <CommandList className="custom-scrollbar max-h-[252px]">
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {filteredOptions().map(option => {
                  // Используем отфильтрованные опции
                  const isSelected = selectedValuesSet.current.has(option)
                  return (
                    <CommandItem
                      key={option}
                      onSelect={() => toggleOption(option)}
                      style={{
                        pointerEvents: 'auto',
                        opacity: 1,
                      }}
                      className={cn(
                        'cursor-pointer py-2 data-[selected=true]:font-medium',
                        isSelected ? 'font-medium' : '',
                      )}
                    >
                      <div
                        className={cn(
                          // 'mr-2 flex h-4 w-4 items-center justify-center',
                          ' flex items-center justify-center gap-2',
                          // isSelected ? 'bg-primary text-primary-foreground ' : 'opacity-50 [&_svg]:invisible',
                        )}
                      >
                        <Checkbox checked={isSelected} />
                        <span>{option}</span>
                      </div>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
            <CommandSeparator />
            <CommandGroup>
              <div className="flex items-center justify-between">
                {selectedValues.length > 0 && (
                  <>
                    <CommandItem
                      onSelect={() => {
                        setSelectedValues([])
                        selectedValuesSet.current.clear()
                        onValueChange([])
                      }}
                      style={{
                        pointerEvents: 'auto',
                        opacity: 1,
                      }}
                      className="flex-1 cursor-pointer justify-center px-3 py-2 hover:rounded-tr-none aria-selected:bg-accent"
                    >
                      Clear
                    </CommandItem>
                    <Separator
                      orientation="vertical"
                      className="flex h-full min-h-6"
                    />
                  </>
                )}
                <CommandItem
                  onSelect={() => setIsPopoverOpen(false)}
                  style={{
                    pointerEvents: 'auto',
                    opacity: 1,
                  }}
                  className="flex-1 cursor-pointer justify-center px-3 py-2 hover:rounded-tr-none aria-selected:bg-accent"
                >
                  Close
                </CommandItem>
              </div>
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    )
  },
)

MultiSelectFormListStringFieldOneLineUiTest.displayName = 'MultiSelectFormListStringFieldOneLineUi'

export default MultiSelectFormListStringFieldOneLineUiTest
